import {GTM_EVENTS} from '../businessAnalysisUtils';
import {industry, noOfEmployees, SHOW_TEAM_STEP} from '../constants';
import {useFieldValueChangeMonitoring, useSignUpTrackingEvents} from '../hooks';
import {useCountryQuery, usePredictAliasQuery} from '../queries';
import {
  StyledContainer,
  StyledForm,
  FlexStartText,
  FormHeader,
} from '../styled';
import {businessLabels, labels} from '../translations';
import {BaseFormProps} from '../types';
import {useMapAndSortCountries} from '../utils';
import {Translate, useGlobalization} from '@planday-corp/globalization';
import {
  useValidation,
  validate as v,
  ValidationRules,
} from '@planday-corp/react-model';
import Button from '@planday-corp/ui/actions/Button';
import Field from '@planday-corp/ui/forms/Field';
import CheckboxField from '@planday-corp/ui/forms/fields/CheckboxField';
import SelectField from '@planday-corp/ui/forms/fields/SelectField';
import TextField from '@planday-corp/ui/forms/fields/TextField';
import InlineSpinner from '@planday-corp/ui/indicators/InlineSpinner';
import Text from '@planday-corp/ui/text/Text';
import styled from 'styled-components';

interface BusinessForm {
  companyName: string;
  countryCode: string;
  industry: string;
  employees: string;
}

const rules: ValidationRules<Partial<BusinessForm>> = {
  companyName: [v.required()],
  countryCode: [v.required()],
  industry: [v.required()],
  employees: [v.required()],
};

export default function BusinessForm({form, update, setStep}: BaseFormProps) {
  const {l10n} = useGlobalization();

  const {data: countries, isLoading: isCountryQueryLoading} = useCountryQuery(
    l10n.locale
  );

  const {data: alias, isLoading: isAliasPredictionLoading} =
    usePredictAliasQuery(form.companyName);

  const mappedCountries = useMapAndSortCountries(countries);

  const track = useSignUpTrackingEvents();
  const {monitorValue, isValueDirty} = useFieldValueChangeMonitoring();

  const isLoading = isCountryQueryLoading || isAliasPredictionLoading;
  const {validate, errors} = useValidation(form, rules);

  const handleNextStep = () => {
    if (validate() && !isLoading) {
      if (SHOW_TEAM_STEP) {
        track.next('team');
        setStep('team');
      } else {
        track.next('portalCreationProgress');
        GTM_EVENTS.FORM_SUBMITTED();
        setStep('portalCreationProgress');
      }
    }
  };

  const handlePreviousStep = () => {
    track.previous('account');
    setStep('account');
  };

  return (
    <>
      <FormHeader>
        <FlexStartText size="title1" element="h1">
          {labels.businessStepTitle}
        </FlexStartText>
        <FlexStartText size="label">
          {labels.businessStepSubtitle}
        </FlexStartText>
      </FormHeader>
      <StyledForm>
        <TextField
          name={'companyName'}
          error={errors?.companyName}
          suffix={isAliasPredictionLoading ? <InlineSpinner /> : undefined}
          label={businessLabels.businessName}
          helpText={
            isAliasPredictionLoading
              ? ''
              : alias
                ? `https://${alias}.planday.com`
                : businessLabels.businessNameHelpText
          }
          isRequired={true}
          value={form.companyName}
          onFocus={() => monitorValue(form.companyName)}
          onBlur={() => isValueDirty(form.companyName) && track.businessName()}
          onChange={update}
        />
        <SelectField
          name={'countryCode'}
          isSearchable
          isRequired={true}
          error={errors?.countryCode}
          value={form.countryCode}
          label={businessLabels.businessLocation}
          helpText={businessLabels.businessLocationHelpText}
          onChange={(value, name) => {
            track.countryCode(value);
            update(value, name);
          }}
          isLoading={isCountryQueryLoading}
          options={mappedCountries}
        />
        <SelectField
          name={'industry'}
          error={errors?.industry}
          value={form.industry}
          isRequired={true}
          label={businessLabels.businessType}
          helpText={businessLabels.businessTypeHelpText}
          onChange={(value, name) => {
            track.industry(value);
            update(value, name);
          }}
          options={industry}
        />
        <SelectField
          name={'employees'}
          error={errors?.employees}
          value={form.employees}
          isRequired={true}
          label={businessLabels.employeesNumber}
          onChange={(value, name) => {
            track.employeeSize(value);
            update(value, name);
          }}
          options={noOfEmployees}
        />

        <Field name="marketingCommunications">
          <StyledContainer>
            <CheckboxField
              name="marketingCommunicationsAccepted"
              isRequired={false}
              value={form.marketingCommunicationsAccepted}
              onChange={update}
            />
            <Text value={labels.marketingCommunications} />
          </StyledContainer>
        </Field>
      </StyledForm>

      <StyledButtonWrapper>
        <Button name="previous" onClick={handlePreviousStep}>
          <Translate {...businessLabels.previous} />
        </Button>

        <Button name="next" type="primary" onClick={handleNextStep}>
          <Translate {...labels.next} />
        </Button>
      </StyledButtonWrapper>
    </>
  );
}

const StyledButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;
